import React from 'react'
import { motion } from 'framer-motion'
import { Slide } from '../types/slide'
import BeastImage from './BeastImage'
import AnimatedText from './AnimatedText'
import AnimatedVerse from './AnimatedVerse'

interface PresentationSlideProps {
  slide: Slide
  slideNumber: number
  totalSlides: number
}

const PresentationSlide: React.FC<PresentationSlideProps> = ({ 
  slide, 
  slideNumber, 
  totalSlides 
}) => {
  return (
    <div className="min-h-screen flex items-center justify-center p-4 sm:p-6 md:p-8 pb-24 sm:pb-28 md:pb-32 pt-16 sm:pt-20 md:pt-24 relative overflow-hidden">
      {/* Slide number indicator */}
      <div className="absolute top-2 left-2 sm:top-3 sm:left-3 md:top-4 md:left-4 lg:top-3 lg:left-3 xl:top-4 xl:left-4 bg-slate-800/50 backdrop-blur-sm px-2 py-1 sm:px-2.5 sm:py-1 md:px-3 md:py-1 lg:px-2.5 lg:py-1 xl:px-3 xl:py-1 rounded-full text-xs sm:text-sm md:text-sm lg:text-sm xl:text-sm text-bible-gold z-10">
        {slideNumber} / {totalSlides}
      </div>

      {/* Main content */}
      <div className="max-w-5xl mx-auto text-center px-4 sm:px-6 md:px-8 w-full">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {slide.title && (
            <AnimatedText
              text={slide.title}
              className={`font-bold text-bible-gold mb-3 sm:mb-4 md:mb-6 leading-tight ${
                slideNumber === 1 
                  ? 'text-xl sm:text-2xl md:text-3xl lg:text-4xl' 
                  : 'text-lg sm:text-xl md:text-2xl lg:text-3xl'
              }`}
              delay={0.2}
            />
          )}

          {slide.subtitle && (
            <AnimatedText
              text={slide.subtitle}
              className={`text-blue-200 mb-4 sm:mb-5 md:mb-6 font-light ${
                slideNumber === 1 
                  ? 'text-base sm:text-lg md:text-xl' 
                  : 'text-sm sm:text-base md:text-lg'
              }`}
              delay={0.4}
            />
          )}

          {slide.content && (
            <AnimatedText
              text={slide.content}
              className={`leading-relaxed text-gray-100 mb-4 sm:mb-5 md:mb-6 max-w-4xl mx-auto ${
                slideNumber === 1 
                  ? 'text-sm sm:text-base md:text-lg' 
                  : 'text-sm sm:text-base md:text-lg'
              }`}
              delay={0.6}
            />
          )}

          {slide.verse && slide.verseReference && (
            <div className="mb-8">
              <AnimatedVerse
                verse={slide.verse}
                reference={slide.verseReference}
                className="relative"
              />
            </div>
          )}

          {slide.image && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className={`mb-8 ${slideNumber === 1 ? 'relative' : ''}`}
            >
              <img
                src={slide.image}
                alt={slide.imageAlt || slide.title}
                className={`max-w-full h-auto rounded-xl shadow-2xl mx-auto ${
                  slideNumber === 1 ? 'rounded-2xl shadow-[0_0_50px_rgba(212,175,55,0.3)]' : ''
                }`}
                style={{ 
                  maxHeight: slideNumber === 1 ? '70vh' : '60vh',
                  objectFit: slideNumber === 1 ? 'cover' : 'contain'
                }}
              />
              {slideNumber === 1 && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 1 }}
                  className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl"
                />
              )}
            </motion.div>
          )}

          {slide.beasts && (
            <div className="max-w-6xl mx-auto">
              {slide.beasts.map((beast, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 + index * 0.1 }}
                  className="beast-card hover:scale-105 transition-transform duration-300 mb-8"
                >
                  <div className="flex flex-col lg:flex-row gap-6 items-start">
                    {/* Image Section */}
                    <div className="w-full lg:w-1/2">
                      <BeastImage
                        beastName={beast.name}
                        description={beast.description}
                        className="w-full h-64 sm:h-80 md:h-96 rounded-xl"
                      />
                    </div>
                    
                    {/* Content Section */}
                    <div className="w-full lg:w-1/2 flex flex-col justify-center">
                      <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-bible-gold mb-3 sm:mb-4">
                        {beast.name}
                      </h3>
                      <p className="text-gray-300 text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
                        {beast.description}
                      </p>
                      {beast.verse && (
                        <div className="text-xs sm:text-sm text-bible-gold font-semibold bg-slate-800/50 p-2 sm:p-3 rounded-lg">
                          {beast.verse}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}

          {slide.table && (
            <div className="max-w-5xl mx-auto px-4">
              <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 sm:p-6 md:p-8 overflow-x-auto">
                <table className="w-full text-xs sm:text-sm md:text-base">
                  <thead>
                    <tr className="border-b border-slate-600">
                      <th className="text-left py-2 sm:py-3 px-2 text-bible-gold font-bold">Daniel 2 (Statue)</th>
                      <th className="text-left py-2 sm:py-3 px-2 text-bible-gold font-bold">Daniel 7 (Beasts)</th>
                      <th className="text-left py-2 sm:py-3 px-2 text-bible-gold font-bold">Empire</th>
                      <th className="text-left py-2 sm:py-3 px-2 text-bible-gold font-bold">Timeframe</th>
                    </tr>
                  </thead>
                  <tbody>
                    {slide.table.map((row, index) => (
                      <motion.tr
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                        className="border-b border-slate-700/50 hover:bg-slate-700/20 transition-colors"
                      >
                        <td className="py-2 sm:py-3 px-2 text-gray-200">{row.daniel2}</td>
                        <td className="py-2 sm:py-3 px-2 text-gray-200">{row.daniel7}</td>
                        <td className="py-2 sm:py-3 px-2 text-blue-200 font-medium">{row.empire}</td>
                        <td className="py-2 sm:py-3 px-2 text-gray-300 text-xs sm:text-sm">{row.timeframe}</td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {slide.points && (
            <div className={`max-w-4xl mx-auto px-4 ${slideNumber === 1 ? 'text-center' : 'text-left'}`}>
              <div className="bg-slate-800/30 backdrop-blur-sm border border-slate-700/50 rounded-xl p-4 sm:p-6 md:p-8">
                <ul className="space-y-3 sm:space-y-4">
                  {slide.points.map((point, index) => (
                    <motion.li
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                      className={`flex items-start space-x-2 sm:space-x-3 ${slideNumber === 1 ? 'justify-center' : ''}`}
                    >
                      <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-bible-gold rounded-full mt-1.5 sm:mt-2 flex-shrink-0"></div>
                      <span className={`text-gray-200 leading-relaxed ${
                        slideNumber === 1 
                          ? 'text-sm sm:text-base md:text-lg font-medium' 
                          : 'text-sm sm:text-base md:text-lg'
                      }`}>{point}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  )
}

export default PresentationSlide
